import { useState } from 'react';
import {
  EditOutlined,
  InboxOutlined,
  MailOutlined,
  PlusOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Divider,
  Empty,
  Popconfirm,
  Segmented,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  archiveEmailTemplate,
  getEmailTemplates,
  restoreEmailTemplate,
} from '@/services/emailTemplates.service';
import EmailTemplateModal from './EmailTemplateModal';
import { EMAIL_TEMPLATES_QUERY_KEY } from './constants';
import useEmployeeNoticesT, { errorText } from './useEmployeeNoticesT';

/**
 * "Email templates" section of the company's Templates tile: emails the
 * company writes once (e.g. "a new colleague is joining") and sends from the
 * employee profile. Saves on its own, apart from the document settings form.
 */
export default function EmailTemplatesSection({ companyId, isEdited = true }) {
  const { t, tn } = useEmployeeNoticesT();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('active');
  const [editing, setEditing] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: [EMAIL_TEMPLATES_QUERY_KEY, companyId, 'list'],
    queryFn: () => getEmailTemplates(companyId, true),
    enabled: !!companyId,
    retry: false,
  });
  const items = (data?.items || []).filter((tpl) => tpl.status === status);

  const toggle = useMutation({
    mutationFn: (tpl) =>
      tpl.status === 'archived'
        ? restoreEmailTemplate(tpl._id)
        : archiveEmailTemplate(tpl._id),
    onSuccess: () => {
      message.success(tn('templateSaved'));
      queryClient.invalidateQueries({
        queryKey: [EMAIL_TEMPLATES_QUERY_KEY, companyId],
      });
    },
    onError: (error) => message.error(errorText(error, tn('saveFailed'))),
  });

  return (
    <section className="grid gap-2">
      {/* Same heading as the document templates above it. */}
      <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />
      <div className="mb-1">
        <h4 className="m-0 font-semibold">{tn('sectionTitle')}</h4>
        <Typography.Text type="secondary" className="mt-1 block text-xs">
          {tn('sectionHint')}
        </Typography.Text>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {isEdited && (
            <Button
              type="primary"
              className="btn-primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing({})}
            >
              {tn('newTemplate')}
            </Button>
          )}
          <div className="flex-1" />
          <Segmented
            size="small"
            value={status}
            onChange={setStatus}
            options={[
              { value: 'active', label: tn('statusActive') },
              { value: 'archived', label: tn('statusArchived') },
            ]}
          />
        </div>
      </div>

      {!isLoading && items.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={tn(status === 'active' ? 'empty' : 'emptyArchived')}
        />
      ) : (
        <div className="grid gap-2">
          {items.map((tpl) => (
            <div
              key={tpl._id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-zinc-200 bg-white p-2.5"
            >
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <MailOutlined className="text-zinc-500" />
                <span className="font-semibold">{tpl.name}</span>
                <Tag color="green">{tn(`events.${tpl.event}`)}</Tag>
                <Tag>{tn(`recipientsModes.${tpl.recipientsMode}`)}</Tag>
                <Tag color="blue">{tpl.language?.toUpperCase()}</Tag>
                <span
                  className="min-w-0 truncate text-[12px] text-zinc-500"
                  title={tpl.subject}
                >
                  {tpl.subject}
                </span>
              </div>
              {isEdited && (
                <div className="flex gap-1">
                  <Tooltip title={t('edit', { ns: 'forms' })}>
                    <Button
                      size="small"
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => setEditing(tpl)}
                    />
                  </Tooltip>
                  <Popconfirm
                    okButtonProps={{ className: '!rounded-[50px]' }}
                    cancelButtonProps={{ className: '!rounded-[50px]' }}
                    title={
                      tpl.status === 'archived'
                        ? tn('restoreConfirm')
                        : tn('archiveConfirm')
                    }
                    okText={t('yes', { ns: 'forms' })}
                    cancelText={t('no', { ns: 'forms' })}
                    onConfirm={() => toggle.mutate(tpl)}
                  >
                    <Tooltip
                      title={
                        tpl.status === 'archived'
                          ? tn('restore')
                          : tn('archive')
                      }
                    >
                      <Button
                        size="small"
                        type="text"
                        icon={
                          tpl.status === 'archived' ? (
                            <RollbackOutlined />
                          ) : (
                            <InboxOutlined />
                          )
                        }
                      />
                    </Tooltip>
                  </Popconfirm>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <EmailTemplateModal
        open={!!editing}
        onClose={() => setEditing(null)}
        companyId={companyId}
        template={editing?._id ? editing : null}
      />
    </section>
  );
}
